import { STORAGE_KEYS } from '../shared/constants';

export interface WidgetConfig {
  id: string;
  type: 'ai-realtime' | 'ai-learning' | 'ai-stats' | 'safety-intelligence' | 'system-stats' | 'verification-stats' | 'user-activity' | 'recent-users' | 'system-status' | 'performance';
  position: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  visible: boolean;
  minimized: boolean;
}

export interface DashboardLayout {
  id: string;
  name: string;
  widgets: WidgetConfig[];
  createdAt: string;
  isDefault?: boolean;
}

export interface UserPreferences {
  currentLayout: string;
  layouts: DashboardLayout[];
  theme: 'light' | 'dark';
  autoSave: boolean;
  gridSize: number;
}

class LayoutPreferencesService {
  private readonly storageKey = STORAGE_KEYS.PREFERENCES + '_layout';

  // Default layouts
  private defaultLayouts: DashboardLayout[] = [
    {
      id: 'ai-focused',
      name: '🧠 AI Odaklı',
      isDefault: true,
      createdAt: new Date().toISOString(),
      widgets: [
        {
          id: 'ai-stats',
          type: 'ai-stats',
          position: { x: 0, y: 0, w: 12, h: 2 },
          visible: true,
          minimized: false
        },
        {
          id: 'ai-realtime',
          type: 'ai-realtime',
          position: { x: 0, y: 2, w: 8, h: 6 },
          visible: true,
          minimized: false
        },
        {
          id: 'ai-learning',
          type: 'ai-learning',
          position: { x: 8, y: 2, w: 4, h: 6 },
          visible: true,
          minimized: false
        },
        {
          id: 'safety-intelligence',
          type: 'safety-intelligence',
          position: { x: 0, y: 8, w: 12, h: 6 },
          visible: true,
          minimized: false
        },
        {
          id: 'system-stats',
          type: 'system-stats',
          position: { x: 0, y: 14, w: 12, h: 2 },
          visible: true,
          minimized: false
        }
      ]
    },
    {
      id: 'admin-focused',
      name: '👨‍💼 Admin Odaklı',
      isDefault: true,
      createdAt: new Date().toISOString(),
      widgets: [
        {
          id: 'system-stats',
          type: 'system-stats',
          position: { x: 0, y: 0, w: 12, h: 2 },
          visible: true,
          minimized: false
        },
        {
          id: 'verification-stats',
          type: 'verification-stats',
          position: { x: 0, y: 2, w: 6, h: 4 },
          visible: true,
          minimized: false
        },
        {
          id: 'user-activity',
          type: 'user-activity',
          position: { x: 6, y: 2, w: 6, h: 4 },
          visible: true,
          minimized: false
        },
        {
          id: 'recent-users',
          type: 'recent-users',
          position: { x: 0, y: 6, w: 8, h: 4 },
          visible: true,
          minimized: false
        },
        {
          id: 'ai-realtime',
          type: 'ai-realtime',
          position: { x: 8, y: 6, w: 4, h: 4 },
          visible: true,
          minimized: true
        }
      ]
    },
    {
      id: 'balanced',
      name: '⚖️ Dengeli',
      isDefault: true,
      createdAt: new Date().toISOString(),
      widgets: [
        {
          id: 'ai-stats',
          type: 'ai-stats',
          position: { x: 0, y: 0, w: 8, h: 2 },
          visible: true,
          minimized: false
        },
        {
          id: 'system-stats',
          type: 'system-stats',
          position: { x: 8, y: 0, w: 4, h: 2 },
          visible: true,
          minimized: false
        },
        {
          id: 'ai-realtime',
          type: 'ai-realtime',
          position: { x: 0, y: 2, w: 6, h: 5 },
          visible: true,
          minimized: false
        },
        {
          id: 'ai-learning',
          type: 'ai-learning',
          position: { x: 6, y: 2, w: 3, h: 5 },
          visible: true,
          minimized: false
        },
        {
          id: 'verification-stats',
          type: 'verification-stats',
          position: { x: 9, y: 2, w: 3, h: 2.5 },
          visible: true,
          minimized: false
        },
        {
          id: 'user-activity',
          type: 'user-activity',
          position: { x: 9, y: 4.5, w: 3, h: 2.5 },
          visible: true,
          minimized: false
        }
      ]
    }
  ];

  // Get user preferences
  getPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const preferences = JSON.parse(stored);
        return {
          ...preferences,
          layouts: [...this.defaultLayouts, ...(preferences.layouts || [])]
        };
      }
    } catch (error) {
      console.error('Error loading layout preferences:', error);
    }

    // Return default preferences
    return {
      currentLayout: 'ai-focused',
      layouts: this.defaultLayouts,
      theme: 'light',
      autoSave: true,
      gridSize: 12
    };
  }

  // Save user preferences
  savePreferences(preferences: UserPreferences): void {
    try {
      // Filter out default layouts before saving
      const customLayouts = preferences.layouts.filter(layout => !layout.isDefault);
      const toSave = {
        ...preferences,
        layouts: customLayouts
      };
      localStorage.setItem(this.storageKey, JSON.stringify(toSave));
      console.log('✅ Layout preferences saved');
    } catch (error) {
      console.error('❌ Error saving layout preferences:', error);
    }
  }

  // Get current layout
  getCurrentLayout(): DashboardLayout | null {
    const preferences = this.getPreferences();
    return preferences.layouts.find(layout => layout.id === preferences.currentLayout) || null;
  }

  // Switch layout
  switchLayout(layoutId: string): void {
    const preferences = this.getPreferences();
    preferences.currentLayout = layoutId;
    this.savePreferences(preferences);
  }

  // Save custom layout
  saveCustomLayout(layout: DashboardLayout): void {
    const preferences = this.getPreferences();
    
    // Remove existing layout with same ID
    preferences.layouts = preferences.layouts.filter(l => l.id !== layout.id);
    
    // Add new layout
    preferences.layouts.push({
      ...layout,
      createdAt: new Date().toISOString(),
      isDefault: false
    });

    this.savePreferences(preferences);
  }

  // Delete custom layout
  deleteLayout(layoutId: string): void {
    const preferences = this.getPreferences();
    const layout = preferences.layouts.find(l => l.id === layoutId);
    
    if (layout && !layout.isDefault) {
      preferences.layouts = preferences.layouts.filter(l => l.id !== layoutId);
      
      // If current layout is deleted, switch to default
      if (preferences.currentLayout === layoutId) {
        preferences.currentLayout = 'ai-focused';
      }
      
      this.savePreferences(preferences);
    }
  }

  // Update widget position
  updateWidgetPosition(widgetId: string, position: WidgetConfig['position']): void {
    const preferences = this.getPreferences();
    const currentLayout = this.getCurrentLayout();
    
    if (currentLayout) {
      const widget = currentLayout.widgets.find(w => w.id === widgetId);
      if (widget) {
        widget.position = position;
        this.saveCustomLayout(currentLayout);
      }
    }
  }

  // Toggle widget visibility
  toggleWidgetVisibility(widgetId: string): void {
    const preferences = this.getPreferences();
    const currentLayout = this.getCurrentLayout();
    
    if (currentLayout) {
      const widget = currentLayout.widgets.find(w => w.id === widgetId);
      if (widget) {
        widget.visible = !widget.visible;
        this.saveCustomLayout(currentLayout);
      }
    }
  }

  // Toggle widget minimized state
  toggleWidgetMinimized(widgetId: string): void {
    const preferences = this.getPreferences();
    const currentLayout = this.getCurrentLayout();
    
    if (currentLayout) {
      const widget = currentLayout.widgets.find(w => w.id === widgetId);
      if (widget) {
        widget.minimized = !widget.minimized;
        this.saveCustomLayout(currentLayout);
      }
    }
  }

  // Reset to default layout
  resetToDefault(layoutId: string = 'ai-focused'): void {
    const preferences = this.getPreferences();
    preferences.currentLayout = layoutId;
    this.savePreferences(preferences);
  }

  // Export layout
  exportLayout(layoutId: string): string {
    const layout = this.getPreferences().layouts.find(l => l.id === layoutId);
    if (layout) {
      return JSON.stringify(layout, null, 2);
    }
    throw new Error('Layout not found');
  }

  // Import layout
  importLayout(layoutData: string): void {
    try {
      const layout: DashboardLayout = JSON.parse(layoutData);
      layout.id = `imported-${Date.now()}`;
      layout.name = `📥 ${layout.name || 'Imported Layout'}`;
      this.saveCustomLayout(layout);
    } catch (error) {
      throw new Error('Invalid layout data');
    }
  }
}

// Export singleton
export const layoutPreferencesService = new LayoutPreferencesService();
export default layoutPreferencesService;
